// Helper function to convert meters to miles with explanation tooltip
import customParseFormat from 'dayjs/plugin/customParseFormat';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import d from 'dayjs';
import duration from 'dayjs/plugin/duration';
import updateLocale from 'dayjs/plugin/updateLocale';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

d.extend(updateLocale);
d.extend(duration);
d.extend(customParseFormat);
d.extend(weekOfYear);
d.updateLocale('en', {
  weekStart: 1, // weeks start on monday
});

export const dayjs = d;

export const metersToMiles = (meters: number) =>
  Number((meters * 0.000621371).toFixed(2));

// Format meters → "X.XX mi" (or fallback if zero/missing)
export const formatDistance = (meters?: number, fallback = 'N/A'): string => {
  if (!meters || meters === 0) return fallback;
  return `${metersToMiles(meters).toFixed(2)} mi`;
};

// Format seconds → "M:SS" (or fallback if zero/missing)
export const formatDuration = (seconds?: number, fallback = 'N/A'): string => {
  if (!seconds || seconds === 0) return fallback;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

// Format (distance in meters, time in seconds) → "M:SS/mi" (or fallback)
export const formatPace = (
  distanceMeters?: number,
  timeSeconds?: number,
  fallback = 'N/A',
): string => {
  if (!distanceMeters || !timeSeconds || distanceMeters === 0) return fallback;
  const miles = distanceMeters / 1609.34;
  if (miles === 0) return fallback;
  const paceInSeconds = timeSeconds / miles;
  const minutes = Math.floor(paceInSeconds / 60);
  const seconds = Math.round(paceInSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}/mi`;
};

export const isSwimWorkout = (type?: string | null): boolean =>
  type?.toLowerCase().includes('swim') ?? false;

export const formatFriendlyDuration = (
  seconds?: number,
  fallback = 'N/A',
): string => {
  if (!seconds || seconds <= 0) return fallback;

  const totalMinutes = Math.max(1, Math.round(seconds / 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  }

  if (minutes > 0 || hours === 0) {
    parts.push(`${minutes} min${minutes === 1 ? '' : 's'}`);
  }

  return parts.join(' ');
};

export const formatSwimPace = (
  distanceMeters?: number,
  timeSeconds?: number,
  fallback = 'N/A',
): string => {
  if (
    !distanceMeters ||
    !timeSeconds ||
    distanceMeters <= 0 ||
    timeSeconds <= 0
  ) {
    return fallback;
  }

  const yards = distanceMeters * 1.09361;
  const paceInSeconds = Math.round(timeSeconds / (yards / 100));
  const minutes = Math.floor(paceInSeconds / 60);
  const seconds = paceInSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}/100 yd`;
};

export const formatWorkoutPace = (
  type?: string | null,
  distanceMeters?: number,
  timeSeconds?: number,
  fallback = 'N/A',
): string =>
  isSwimWorkout(type)
    ? formatSwimPace(distanceMeters, timeSeconds, fallback)
    : formatPace(distanceMeters, timeSeconds, fallback);

// Format a timestamp → "ddd, MMM D · h:mm A"
export const formatStartDateTime = (timestamp: any): string => {
  if (!timestamp) return '';
  const d = dayjs(timestamp);
  if (!d.isValid()) return '';
  return d.format('ddd, MMM D · h:mm A');
};

// Format meters → "X ft" (or null if missing)
export const formatFeet = (meters?: number): string | null => {
  if (!meters || meters <= 0) return null;
  return `${Math.round(meters * 3.28084)} ft`;
};

// Format heart rate → "X bpm" (or null if missing)
export const formatBpm = (heartRate?: number): string | null => {
  if (!heartRate || heartRate <= 0) return null;
  return `${Math.round(heartRate)} bpm`;
};

// Format cadence → "X spm" (or null if missing)
export const formatCadence = (cadence?: number): string | null => {
  if (!cadence || cadence <= 0) return null;
  return `${Math.round(cadence)} spm`;
};

export const hasDisplayValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return Number.isFinite(value) && value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (
      normalized === '' ||
      normalized === '0' ||
      normalized === 'null' ||
      normalized === 'undefined' ||
      normalized === 'n/a' ||
      normalized === 'na' ||
      normalized === 'none'
    ) {
      return false;
    }

    const numericMatch = normalized.match(/^(-?\d+(?:\.\d+)?)\s*(.*)$/);
    if (numericMatch && Number(numericMatch[1]) === 0) return false;

    return true;
  }
  return true;
};

export const formatOptionalMiles = (meters?: number | null): string => {
  if (!hasDisplayValue(meters)) return '—';
  return `${metersToMiles(meters as number).toFixed(2)} mi`;
};

export const parsePlannedDate = (date: string) => {
  const calendarDate = date.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  if (calendarDate) {
    // Planned dates represent calendar days. Parse the UTC date portion as a
    // local calendar date so clients west of UTC do not display the day before.
    return dayjs(calendarDate, 'YYYY-MM-DD', true);
  }
  return dayjs(date);
};

export function formatTime({ seconds }: { seconds: number }) {
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0
      ? `${minutes} minute${minutes !== 1 ? 's' : ''}, ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`
      : `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const remainingMinutes = Math.floor((seconds % 3600) / 60);
    return remainingMinutes > 0
      ? `${hours} hour${hours !== 1 ? 's' : ''}, ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`
      : `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
}

// get the planned distance vs. completed distance
// Helper function to extract numbers from strings like "5 miles" or "10km"
export const parseNumberFromString = (str: string): number => {
  const match = str.match(/\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : 0;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Safe date formatting for mobile compatibility
export function safeFormatDate(
  date: any,
  fallback: string = 'Invalid date',
): string {
  if (!date) return fallback;

  try {
    // Try dayjs first
    const dayjsDate = dayjs(date);
    if (dayjsDate.isValid()) {
      return dayjsDate.format('MM/DD/YYYY');
    }

    // Fallback to native Date
    const nativeDate = new Date(date);
    if (!isNaN(nativeDate.getTime())) {
      return nativeDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    }

    // Log for debugging on mobile
    if (process.env.NODE_ENV === 'development') {
      console.warn('Date formatting failed for:', date, typeof date);
    }

    return fallback;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Date formatting error:', error, 'for date:', date);
    }
    return fallback;
  }
}
